import { memo, useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';

import type { IdAdMediumDto, IdPlacementDto } from '@/services/ws-broadcast/src/main/resources/rest-api';
import type { CampaignPositionType } from '@/services/ws-orders/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { Filter } from '@/js/libs/ui/src/V2';

import type { Option } from 'src/types';
import { useBroadcastPlacementOptions } from '../hooks/useBroadcastPlacementOptions';

interface BroadcastPlacementFilterProps {
  campaignType: CampaignPositionType.TV | CampaignPositionType.RADIO;
  adMediumId: IdAdMediumDto | undefined;
  value: Option<IdPlacementDto> | null;
  onChange: (value: Option<IdPlacementDto> | null) => void;
  label?: string;
  dataTestId?: string;
}

export const BroadcastPlacementFilter: FC<BroadcastPlacementFilterProps> = memo(function BroadcastPlacementFilter({
  campaignType,
  adMediumId,
  value,
  onChange,
  label,
  dataTestId,
}) {
  const [t] = useTranslation(['common']);
  const {
    options,
    pending: loading,
    total,
    searchFilter,
    setSearchFilter,
  } = useBroadcastPlacementOptions({
    campaignType,
    adMediumId,
  });

  const handleChange = useCallbackRef((newValue: Option<IdPlacementDto> | null) => {
    onChange(newValue);
  });

  const serverSideFiltering = useMemo(
    () => ({ filter: searchFilter, setFilter: setSearchFilter, total }),
    [searchFilter, setSearchFilter, total],
  );

  return (
    <Filter<IdPlacementDto>
      label={label || t('common:PLACEMENT')}
      loading={loading}
      selected={value}
      onChange={handleChange}
      serverSideFiltering={serverSideFiltering}
      options={options}
      dataTestId={dataTestId || 'broadcast-placement-dropdown'}
    />
  );
});
